import React from 'react';

import cn from 'classnames';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { EventJournalItemList } from '@entities/eventJournal';
import { useGetJournalEventsListQuery } from '@entities/eventJournal/api/hooks';
import { useGetAllQueryParams } from '@entities/eventJournal/lib/hooks';
import { DateHelpers } from '@shared/helpers';
import { ReactErrorFallback, Skeleton } from '@shared/ui';
import {
    useGetInitialQueryStateInfo,
    useConditionalRendering,
} from '@widgets/EventJournal/lib/hooks';

import cls from './EventJournalBody.module.css';
import {
    EmptyEventsListPlaceholder,
    EventJournalItemListHeader,
    NotFoundEventsPlaceholder,
} from '..';

interface EventJournalBodyProps {
    canCreateCustomEvent: boolean;
    isOwner: boolean;
    classes?: string;
}

export const EventJournalBody = ({
    canCreateCustomEvent,
    isOwner,
    classes,
}: EventJournalBodyProps) => {
    const { dateFormatter } = DateHelpers;
    const { t } = useTranslation();

    const { data, isPending, isFetching, isPlaceholderData, isFetched } =
        useGetJournalEventsListQuery();
    const { params } = useGetAllQueryParams();

    const { isOnlyTwoParams, hasSortProp } = useGetInitialQueryStateInfo({ params });

    const { showInitialEmpty, showSkeleton, showFilteredNotFound, showList } =
        useConditionalRendering({
            data,
            isPlaceholderData,
            isFetched,
            isFetching,
            isOnlyTwoParams,
            hasSortProp,
            isPending,
        });

    return (
        <section className={cn(cls.EventJournalBody, [classes])}>
            {showSkeleton && <Skeleton width="100%" height={787} border="8px" />}
            {showList &&
                data?.map((item) => (
                    <EventJournalItemList
                        key={item.date}
                        photoEvents={item?.events?.photo ?? []}
                        textEvents={item?.events?.text ?? []}
                        header={
                            <EventJournalItemListHeader
                                title={dateFormatter({ date: item.date, format: 'DD MMMM YYYY' })}
                                weatherData={item?.weather}
                            />
                        }
                        classes={cn(isFetching && cls.isFetching)}
                    />
                ))}
            {showInitialEmpty && (
                <EmptyEventsListPlaceholder
                    t={t}
                    canCreateCustomEvent={canCreateCustomEvent}
                    isOwner={isOwner}
                />
            )}

            {showFilteredNotFound && <NotFoundEventsPlaceholder t={t} />}
        </section>
    );
};

export default withErrorBoundary(EventJournalBody, {
    FallbackComponent: () => <ReactErrorFallback classes={cls.reactError} />,
});
